/**
 * My applications.
 *
 * Ordered by what the captain needs to act on, not by date: anything live sits
 * above anything finished. A completed cruise from July should not push
 * tomorrow's approval below the fold.
 */

import { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Empty, Notice, Spinner, StateBadge } from '@abg/ui'
import { formatWindowForCaptain, relative } from '@abg/ui/time'
import { api } from '../lib/api'
import { cached, listOutbox, type OutboxEntry } from '../lib/offline'
import type { Application } from '../lib/types'

const LIVE = ['draft', 'submitted', 'with_csc', 'approved', 'active']

export function ApplicationsPage() {
  const location = useLocation()
  const queued = (location.state as { queued?: boolean } | null)?.queued
  const [applications, setApplications] = useState<Application[]>([])
  const [pending, setPending] = useState<OutboxEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [stale, setStale] = useState(false)

  useEffect(() => {
    let cancelled = false
    void (async () => {
      const result = await cached('applications', () =>
        api.get<Application[]>('/api/applications'),
      )
      if (cancelled) return
      setApplications(result.data)
      setStale(result.stale)
      setPending(await listOutbox())
      setLoading(false)
    })().catch(() => setLoading(false))
    return () => {
      cancelled = true
    }
  }, [])

  const live = applications.filter((a) => LIVE.includes(a.state))
  const past = applications.filter((a) => !LIVE.includes(a.state))

  return (
    <div className="page">
      <header className="page-header">
        <h1>My applications</h1>
        {stale && <span className="subtitle">Saved data — not refreshed since you were online.</span>}
      </header>

      {queued && (
        <Notice tone="warn">
          Saved on this device. It will be sent automatically as soon as you have a
          connection.
        </Notice>
      )}

      {pending.length > 0 && (
        <section className="card">
          <strong>Waiting to send</strong>
          <ul className="list" style={{ marginTop: 'var(--space-2)', listStyle: 'none', padding: 0 }}>
            {pending.map((entry) => (
              <li key={entry.id} className="row-between small">
                <span>{entry.label}</span>
                <span className="caption">
                  {entry.attempts > 0 ? `${entry.attempts} attempts` : 'queued'}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {loading && <Spinner label="Loading…" />}

      {!loading && applications.length === 0 && pending.length === 0 && (
        <Empty>
          Nothing yet. Find a spot on the chart and apply — we handle the harbour authority.
        </Empty>
      )}

      {live.length > 0 && (
        <section className="list">
          {live.map((application) => (
            <ApplicationRow key={application.id} application={application} />
          ))}
        </section>
      )}

      {past.length > 0 && (
        <>
          <h2 className="eyebrow" style={{ marginTop: 'var(--space-4)' }}>
            Finished
          </h2>
          <section className="list">
            {past.map((application) => (
              <ApplicationRow key={application.id} application={application} />
            ))}
          </section>
        </>
      )}

      <Link to="/apply" className="btn btn-lg btn-block">
        New application
      </Link>
    </div>
  )
}

function ApplicationRow({ application }: { application: Application }) {
  return (
    <Link to={`/applications/${application.id}`} className="list-row">
      <div className="row-between">
        <span className="title">
          {application.anchorage_name ?? 'Unregistered position'}
        </span>
        <StateBadge state={application.state} />
      </div>
      <div className="small muted">
        {formatWindowForCaptain(application.window_start, application.window_end)}
      </div>
      <div className="caption row-between">
        <span>{application.reference}</span>
        {application.state === 'approved' && (
          <span>starts {relative(application.window_start)}</span>
        )}
        {application.filed_by_agent && <span>filed for you</span>}
      </div>
    </Link>
  )
}
